/**
 * useIdleTimer
 *
 * Fires onIdle()  after idleMs of inactivity  (default 15 min)
 * Fires onLogout() after logoutMs of inactivity (default 30 min)
 *
 * Activity events: mousemove, mousedown, keydown, touchstart, scroll, visibilitychange
 * Calling reset() restarts both timers (used when the user unlocks the session).
 */
import { useEffect, useRef, useCallback } from "react";

const ACTIVITY_EVENTS = [
  "mousemove", "mousedown", "keydown",
  "touchstart", "scroll", "visibilitychange",
];

export default function useIdleTimer({
  onIdle,
  onLogout,
  idleMs    = 15 * 60 * 1000,   // 15 minutes
  logoutMs  = 30 * 60 * 1000,   // 30 minutes
  enabled   = true,
}) {
  const idleTimer   = useRef(null);
  const logoutTimer = useRef(null);

  const clearTimers = useCallback(() => {
    clearTimeout(idleTimer.current);
    clearTimeout(logoutTimer.current);
  }, []);

  const reset = useCallback(() => {
    if (!enabled) return;
    clearTimers();
    idleTimer.current   = setTimeout(onIdle,   idleMs);
    logoutTimer.current = setTimeout(onLogout, logoutMs);
  }, [enabled, clearTimers, onIdle, onLogout, idleMs, logoutMs]);

  useEffect(() => {
    if (!enabled) return;

    reset();

    const handle = () => reset();
    ACTIVITY_EVENTS.forEach(ev => window.addEventListener(ev, handle, { passive: true }));

    return () => {
      clearTimers();
      ACTIVITY_EVENTS.forEach(ev => window.removeEventListener(ev, handle));
    };
  }, [enabled, reset, clearTimers]);

  return { reset };
}
